import { UseFormReturn } from "react-hook-form";
import { useRouter } from "src/hooks/useRouter";
import { useModal } from "src/hooks/useModal";
import { Spinner } from "react-bootstrap";
import "./Join.style.scss";
import MsgModal from "components/Common/Modal/MsgModal";
import ProductCard from "components/Login/ProductCard";
import { JoinFormData } from "./Join.schema";
import FormInput from "components/Common/Form/Input/FormInput";
import { FormContainer } from "components/Common/Form/Input/FormContainer";
import Checkbox from "components/Common/Form/Checkbox";
import { SubmitBtn } from "components/Common/Form/SubmitBtn";
import { useTranslation } from "react-i18next";

interface IJoinUIProps {
  // 폼 관련
  joinForm: UseFormReturn<JoinFormData>;
  onJoinSubmit: (data: JoinFormData) => void;
  joinPossible: boolean;

  // 상태
  msgModalTitle: string;
  msgModalBody: string;
  showMsgModal: boolean;
  isSendTokenLoading: boolean;
  isSendTokenSuccess: boolean;
  isCheckTokenLoading: boolean;
  isCheckTokenSuccess: boolean;

  // 핸들러
  sendToken: () => void; // 이메일 인증 토큰 전송
  checkToken: () => void; // 인증 토큰 확인
  nextButton: () => void;
  // handleUserIdCheck: (userId: string) => void;
}

const JoinUI = (props: IJoinUIProps) => {
  const {
    // 폼 관련
    joinForm,
    onJoinSubmit,
    joinPossible,

    // 상태
    msgModalTitle,
    msgModalBody,
    showMsgModal,
    isSendTokenLoading,
    isSendTokenSuccess,
    isCheckTokenLoading,
    isCheckTokenSuccess,

    // 핸들러
    sendToken,
    checkToken,
    nextButton,
    // handleUserIdCheck,
  } = props;

  const { goLogin, lang } = useRouter();
  const { openTermsService, openPrivacyPolicy, openInformationAgree } = useModal();
  const { t } = useTranslation();

  // 개별 약관 동의 상태를 관찰하여 '모두 동의' 체크박스의 상태를 동기화하기 위함
  const termsAgreed = joinForm.watch("termsAgreed");
  const privacyAgreed = joinForm.watch("privacyAgreed");
  const isAllAgreed = termsAgreed && privacyAgreed;

  // '모두 동의' 클릭 시 개별 약관 폼 데이터를 일괄 업데이트하여 사용자 편의성 제공
  const handleAllAgree = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    joinForm.setValue("termsAgreed", checked, { shouldValidate: true });
    joinForm.setValue("privacyAgreed", checked, { shouldValidate: true });
  };

  return (
    <div className="joinBody">
      <div className="joinContainer">
        <ProductCard />
        <div className="joinCard">
          <div className="tabContainer">
            <div className="tab active">
              <span className="tabText">{t("logIn.signUp.title")}</span>
            </div>
            <div className="tab" onClick={goLogin}>
              <span className="tabText">{t("logIn.goToLoginBtn")}</span>
            </div>
          </div>

          <FormContainer onSubmit={joinForm.handleSubmit(onJoinSubmit)}>
            {/* 이메일 주소 */}
            <FormInput
              label={t("navBar.myAccount.email")}
              name="userId"
              register={joinForm.register}
              errors={joinForm.formState.errors}
              placeholder={t("logIn.enterUserEmail")}
              type="email"
              disabled={isSendTokenLoading || isSendTokenSuccess}
              isClearBtn={false}
            >
              <button
                type="button"
                onClick={sendToken}
                disabled={
                  isSendTokenLoading ||
                  isSendTokenSuccess ||
                  !!joinForm.formState.errors.userId
                }
                className="btn btn-gray width mg ml-10 sendBtn"
              >
                <span>
                  {isSendTokenLoading ? (
                    <>
                      <Spinner animation="border" role="status" size="sm" />
                      <span className="mg ml-6">Sending...</span>
                    </>
                  ) : (
                    t("logIn.signUp.otp.sendBtn")
                  )}
                </span>
              </button>
            </FormInput>

            {/* 인증 토큰 입력 */}
            <FormInput
              name="token"
              label={t("logIn.signUp.otp.title")}
              register={joinForm.register}
              errors={joinForm.formState.errors}
              placeholder={t("logIn.signUp.otp.enterMsg")}
              disabled={isCheckTokenLoading || isCheckTokenSuccess}
              success={{
                isSuccess: isCheckTokenSuccess,
                message: t("logIn.signUp.otp.successMsg"),
              }}
              isClearBtn={false}
            >
              <button
                type="button"
                onClick={checkToken}
                disabled={isCheckTokenLoading || isCheckTokenSuccess}
                className="btn btn-gray width mg ml-10 sendBtn"
              >
                <span>
                  {isCheckTokenLoading ? (
                    <>
                      <Spinner animation="border" role="status" size="sm" />
                      <span className="mg ml-6">Sending...</span>
                    </>
                  ) : (
                    t("logIn.signUp.otp.confirmBtn")
                  )}
                </span>
              </button>
            </FormInput>

            {/* 이름 입력 */}
            <FormInput
              label={t("logIn.signUp.name")}
              name="userName"
              register={joinForm.register}
              errors={joinForm.formState.errors}
              placeholder={t("logIn.signUp.name")}
            />

            {/* 회사명 입력 */}
            <FormInput
              label={t("logIn.signUp.companyName")}
              name="companyName"
              register={joinForm.register}
              errors={joinForm.formState.errors}
              placeholder={t("logIn.signUp.companyName")}
            />

            {/* 비밀번호 입력 */}
            <FormInput
              label={t("logIn.signUp.password")}
              name="password"
              type="password"
              register={joinForm.register}
              errors={joinForm.formState.errors}
              placeholder={t("logIn.signUp.password")}
              hint={t("logIn.signUp.passwordHint")}
            />

            {/* 비밀번호 확인 입력 */}
            <FormInput
              // label={t("logIn.signUp.confirmPassowrd")}
              label={t("logIn.signUp.confirmPassowrd")}
              name="passwordCheck"
              type="password"
              register={joinForm.register}
              errors={joinForm.formState.errors}
              placeholder={t("logIn.signUp.enterConfirmPassowrd")}
            />

            {/* 약관 동의 체크박스 */}
            <div className="termsGroup" style={{ display: "flex", flexDirection: "column" }}>
              <div style={{ paddingBottom: "12px" }}>
                <Checkbox
                  name="allAgree"
                  checked={isAllAgreed}
                  onChange={handleAllAgree}
                >
                  <label htmlFor="allAgree" className="checkboxLabel" style={{ cursor: "pointer", fontWeight: "700" }}>
                    {t("logIn.signUp.agreeAll")}
                  </label>
                </Checkbox>
              </div>
              <hr style={{ margin: "0 0 16px 0", borderTop: "1px solid #2b2b2b" }} />
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <Checkbox
                  name="termsAgreed"
                  register={joinForm.register}
                  errors={joinForm.formState.errors}
                >
                  <label htmlFor="termsAgreed" className="checkboxLabel" style={{ cursor: "pointer" }}>
                    <span>{`[${t("logIn.signUp.required")}]`}&nbsp;</span>{lang === "en" && "I agree to the "}
                    <a onClick={(e) => { e.preventDefault(); openTermsService(); }} style={{ textDecoration: "underline", color: "#252d38", fontWeight: "500" }}>{t("siteType.agreement2")}</a>{lang === "ko" && "에 동의합니다."}
                  </label>
                </Checkbox>
                <Checkbox
                  name="privacyAgreed"
                  register={joinForm.register}
                  errors={joinForm.formState.errors}
                >
                  <label htmlFor="privacyAgreed" className="checkboxLabel" style={{ cursor: "pointer" }}>
                    <span>{`[${t("logIn.signUp.required")}]`}&nbsp;</span>{lang === "en" && "I accept the "}
                    <a onClick={(e) => {
                      e.preventDefault(); lang === "ko" ? openInformationAgree()
                        : openPrivacyPolicy();
                    }} style={{ textDecoration: "underline", color: "#252d38", fontWeight: "500" }}>
                      {lang === "ko" ? "개인정보 수집 및 이용" : "Privacy Policy"}</a>
                    {lang === "ko" && "에 동의합니다."}
                  </label>
                </Checkbox>
              </div>
            </div>

            {/* 회원가입 버튼 */}
            <SubmitBtn
              text={t("logIn.signUp.goToSignUpBtn")}
              disabled={!joinPossible || !isCheckTokenSuccess}
            />
            {/* <div className="buttonContainer">
              <button
                type="submit"
                className="joinButton"
                disabled={!joinPossible}
              >
                <span>{t("logIn.signUp.goToSignUpBtn")}</span>
              </button>
            </div> */}

            {/* 로그인 링크 */}
            <div className="formDescription">
              <span className="formDescriptionText">
                {t("logIn.signUp.checkMember")}
              </span>
              <span className="formDescriptionLink">
                <a onClick={goLogin}>{t("logIn.signUp.goToLogin")}</a>
              </span>
            </div>
          </FormContainer>
        </div>
      </div>

      {/* 성공 모달 */}
      {showMsgModal && (
        <MsgModal
          msgModalTitle={msgModalTitle}
          msgModalBody={msgModalBody}
          showMsgModal={showMsgModal}
          nextButton={nextButton}
        />
      )}
    </div>
  );
};

export default JoinUI;
